import { z } from "zod";

const IdentifierSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const TimestampSchema = z.iso.datetime({ offset: true });
const CoordinatesSchema = z.tuple([
  z.number().min(-180).max(180),
  z.number().min(-90).max(90),
]);
const ElevatedCoordinatesSchema = z.tuple([
  z.number().min(-180).max(180),
  z.number().min(-90).max(90),
  z.number(),
]);

const GeometrySourceSchema = z.discriminatedUnion("provider", [
  z
    .object({
      provider: z.literal("openstreetmap"),
      osmElementId: z.number().int().positive(),
      sourceUrl: z.url(),
      retrievedAt: TimestampSchema,
      attribution: z.literal("© OpenStreetMap contributors, ODbL 1.0"),
    })
    .strict(),
  z
    .object({
      provider: z.literal("f1-circuits"),
      sourceUrl: z.url(),
      retrievedAt: TimestampSchema,
      revision: z.string().regex(/^[0-9a-f]{40}$/),
      attribution: z.string().trim().min(1).max(500),
    })
    .strict(),
]);

export const CircuitAssetSchema = z
  .object({
    schemaVersion: z.literal(1),
    circuitId: IdentifierSchema,
    mode: z.enum(["3d", "2d-fallback"]),
    centerline: z.array(ElevatedCoordinatesSchema).min(4).max(20_000),
    bounds: z.tuple([CoordinatesSchema, CoordinatesSchema]),
    lengthMeters: z.number().positive(),
    elevationRangeMeters: z.number().nonnegative(),
    geometrySource: GeometrySourceSchema,
    bankAngleDegrees: z.array(z.number().min(-45).max(45)).optional(),
    bankingSource: z
      .object({
        provider: z.literal("circuit-zandvoort"),
        sourceUrl: z.url(),
        retrievedAt: TimestampSchema,
        attribution: z.string().trim().min(1).max(500),
      })
      .strict()
      .optional(),
    elevationSource: z
      .object({
        provider: z.literal("mapzen-terrain-tiles"),
        accessedAt: TimestampSchema,
        sourceKeys: z.array(z.string().trim().min(1)).min(1).max(20),
        attribution: z.string().trim().min(1).max(500),
      })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((asset, context) => {
    if (asset.mode === "3d" && asset.elevationSource === undefined) {
      context.addIssue({
        code: "custom",
        message: "A 3D circuit requires elevation provenance.",
        path: ["elevationSource"],
      });
    }
    if (asset.mode === "2d-fallback" && asset.elevationSource !== undefined) {
      context.addIssue({
        code: "custom",
        message: "A 2D fallback cannot claim elevation provenance.",
        path: ["elevationSource"],
      });
    }
    if (
      asset.bankAngleDegrees !== undefined &&
      asset.bankAngleDegrees.length !== asset.centerline.length
    ) {
      context.addIssue({
        code: "custom",
        message: "Bank angle samples must align with the centerline.",
        path: ["bankAngleDegrees"],
      });
    }
    if (
      asset.bankingSource !== undefined &&
      asset.bankAngleDegrees === undefined
    ) {
      context.addIssue({
        code: "custom",
        message: "Banking provenance requires bank angle samples.",
        path: ["bankingSource"],
      });
    }
  });

export type CircuitAsset = z.infer<typeof CircuitAssetSchema>;

interface CircuitGeometryInputBase {
  circuitId: string;
  retrievedAt: string;
  centerline: readonly [longitude: number, latitude: number][];
}

export type CircuitGeometryInput =
  | (CircuitGeometryInputBase & {
      osmElementId: number;
      sourceUrl: string;
      geometrySource?: never;
    })
  | (CircuitGeometryInputBase & {
      geometrySource: {
        provider: "f1-circuits";
        sourceUrl: string;
        retrievedAt: string;
        revision: string;
        attribution: string;
      };
      osmElementId?: never;
      sourceUrl?: never;
    });

export interface ElevationSample {
  meters: number;
  sourceKeys: readonly string[];
}

export interface CircuitPreprocessOptions {
  elevationAt?: (
    coordinates: readonly [longitude: number, latitude: number],
  ) => Promise<ElevationSample>;
  elevationAccessedAt?: string;
  elevationAttribution?: string;
}

export interface CircuitAssetIndexEntry {
  circuitId: string;
  assetState: "3d" | "2d-fallback" | "unavailable";
  assetPath?: string;
  bankingAttribution?: string;
  geometryAttribution: string;
  elevationAttribution?: string;
}

export interface CircuitMaintenanceIssue {
  circuitId: string;
  reason: "missing-verified-geometry";
  title: string;
}

const earthRadiusMeters = 6_371_008.8;
const osmAttribution = "© OpenStreetMap contributors, ODbL 1.0";

function radians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function distanceMeters(
  left: readonly [number, number],
  right: readonly [number, number],
): number {
  const latitudeDelta = radians(right[1] - left[1]);
  const longitudeDelta = radians(right[0] - left[0]);
  const leftLatitude = radians(left[1]);
  const rightLatitude = radians(right[1]);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(leftLatitude) *
      Math.cos(rightLatitude) *
      Math.sin(longitudeDelta / 2) ** 2;
  return 2 * earthRadiusMeters * Math.asin(Math.sqrt(haversine));
}

function deduplicate(
  centerline: readonly [longitude: number, latitude: number][],
): [longitude: number, latitude: number][] {
  return centerline.filter((coordinates, index) => {
    const previous = centerline[index - 1];
    return (
      previous === undefined ||
      coordinates[0] !== previous[0] ||
      coordinates[1] !== previous[1]
    );
  });
}

function validateGeometry(input: CircuitGeometryInput) {
  const coordinates = deduplicate(input.centerline);
  if (coordinates.length < 4) {
    throw new Error(`Circuit ${input.circuitId} needs at least four points.`);
  }
  for (const point of coordinates) CoordinatesSchema.parse(point);
  const first = coordinates[0];
  const last = coordinates.at(-1);
  if (first === undefined || last === undefined) {
    throw new Error(`Circuit ${input.circuitId} has no centerline.`);
  }
  if (distanceMeters(first, last) > 50) {
    throw new Error(`Circuit ${input.circuitId} centerline is not closed.`);
  }
  return coordinates;
}

function geometryMetrics(
  coordinates: readonly [longitude: number, latitude: number][],
) {
  let lengthMeters = 0;
  let minimumLongitude = Infinity;
  let minimumLatitude = Infinity;
  let maximumLongitude = -Infinity;
  let maximumLatitude = -Infinity;
  for (const [index, coordinatesAtIndex] of coordinates.entries()) {
    const previous = coordinates[index - 1];
    if (previous !== undefined) {
      lengthMeters += distanceMeters(previous, coordinatesAtIndex);
    }
    minimumLongitude = Math.min(minimumLongitude, coordinatesAtIndex[0]);
    minimumLatitude = Math.min(minimumLatitude, coordinatesAtIndex[1]);
    maximumLongitude = Math.max(maximumLongitude, coordinatesAtIndex[0]);
    maximumLatitude = Math.max(maximumLatitude, coordinatesAtIndex[1]);
  }
  return {
    bounds: [
      [minimumLongitude, minimumLatitude],
      [maximumLongitude, maximumLatitude],
    ] as const,
    lengthMeters,
  };
}

export async function preprocessCircuit(
  input: CircuitGeometryInput,
  options: CircuitPreprocessOptions = {},
): Promise<CircuitAsset> {
  const coordinates = validateGeometry(input);
  const metrics = geometryMetrics(coordinates);
  let samples: ElevationSample[] | null = null;
  if (options.elevationAt !== undefined) {
    try {
      samples = await Promise.all(
        coordinates.map((point) => options.elevationAt?.(point)),
      ).then((values) =>
        values.every((value) => value !== undefined)
          ? values.map((value) => value)
          : null,
      );
    } catch {
      samples = null;
    }
  }
  const minimumElevation =
    samples === null ? 0 : Math.min(...samples.map((sample) => sample.meters));
  const maximumElevation =
    samples === null ? 0 : Math.max(...samples.map((sample) => sample.meters));
  const sourceKeys = [
    ...new Set(samples?.flatMap((sample) => sample.sourceKeys) ?? []),
  ].sort();
  const hasElevationProvenance =
    samples !== null &&
    sourceKeys.length > 0 &&
    options.elevationAccessedAt !== undefined &&
    options.elevationAttribution !== undefined;
  const geometrySource =
    "geometrySource" in input
      ? input.geometrySource
      : {
          provider: "openstreetmap" as const,
          osmElementId: input.osmElementId,
          sourceUrl: input.sourceUrl,
          retrievedAt: input.retrievedAt,
          attribution: osmAttribution,
        };
  const asset = {
    schemaVersion: 1,
    circuitId: input.circuitId,
    mode: hasElevationProvenance ? "3d" : "2d-fallback",
    centerline: coordinates.map((point, index) => [
      point[0],
      point[1],
      hasElevationProvenance ? (samples?.[index]?.meters ?? 0) : 0,
    ]),
    bounds: metrics.bounds,
    lengthMeters: metrics.lengthMeters,
    elevationRangeMeters: maximumElevation - minimumElevation,
    geometrySource,
    ...(hasElevationProvenance
      ? {
          elevationSource: {
            provider: "mapzen-terrain-tiles",
            accessedAt: options.elevationAccessedAt,
            sourceKeys,
            attribution: options.elevationAttribution,
          },
        }
      : {}),
  };
  return CircuitAssetSchema.parse(asset);
}

export function buildCircuitIndex(
  activeCircuitIds: readonly string[],
  assets: readonly CircuitAsset[],
): {
  entries: CircuitAssetIndexEntry[];
  maintenanceIssues: CircuitMaintenanceIssue[];
} {
  const assetById = new Map(
    assets.map((asset) => [asset.circuitId, CircuitAssetSchema.parse(asset)]),
  );
  const entries = activeCircuitIds.map((circuitId) => {
    const asset = assetById.get(circuitId);
    if (asset === undefined) {
      return {
        circuitId,
        assetState: "unavailable" as const,
        geometryAttribution: "Circuit metadata: Jolpica F1",
      };
    }
    return {
      circuitId,
      assetState: asset.mode,
      assetPath: `./circuits/${circuitId}.json`,
      geometryAttribution: asset.geometrySource.attribution,
      ...(asset.elevationSource === undefined
        ? {}
        : { elevationAttribution: asset.elevationSource.attribution }),
    };
  });
  const maintenanceIssues = entries.flatMap((entry) =>
    entry.assetState === "unavailable"
      ? [
          {
            circuitId: entry.circuitId,
            reason: "missing-verified-geometry" as const,
            title: `Add verified circuit asset for ${entry.circuitId}`,
          },
        ]
      : [],
  );
  return { entries, maintenanceIssues };
}
